import { OmniElement, unsafeStatic, staticHtml } from 'omni-ui';
import { renderError, schemaFieldType } from './util.js';
import { metadata } from '../../om2-form-builder-metadata.js';

// Make it easier for child classes to import these utilities
export {
  mergeClass,
  wrapWithLink,
  renderTooltip,
  renderError,
} from './util.js';

export const SHARED_FIELD = Symbol('Shared Field');

export class Om2FormFieldBase extends OmniElement {
  static get properties() {
    return {
      key: { type: String },
      env: { type: String },
      schema: { type: Object },
      data: { type: Object },
      context: { type: Object },
    };
  }

  /* Stores all registered form field components */
  static registeredFields = {};

  /**
   * Generate an html tag name based on the field type
   */
  static tagName = fieldType => `om2-form-field-${fieldType}`;

  /**
   * Registers a form field component with both the CustomElementRegistry as well as
   * internally so we can look it up when determining which form field component should
   * handle rendering a schema.
   *
   * @static
   * @param {string} fieldType - The type to use for lookup purposes (typically in schema.type)
   */
  static register(fieldType) {
    super.register(this.tagName(fieldType), this);
    this.registeredFields[fieldType] = this;

    if (this.metadata?.unsupported) return;
    if (!this.metadata.config)
      throw new Error(`Field ${fieldType} is missing from field metadata`);

    // Expand any fields marked as shared (SHARED_FIELD) with the common, shared config
    this.metadata.config.fields = this.expandSharedFields(
      this.metadata.config.fields
    );

    // Store the field/layout config in the form builder's metadata
    const store = metadata[this.metadata.layout ? 'layout' : 'fields'];
    const metadataKey = fieldType;
    store[metadataKey] = this.metadata.config;
  }

  /**
   * Static factory method to generate the appropriate template for a form field based
   * on the provided schema.
   *
   * @static
   * @param {object} opts - Options argument
   * @param {object} opts.schema - The schema object
   * @param {string} opts.key - The unique key to use for this field
   * @param {string} opts.data - Saved data for this field
   * @param {string} opts.env - The current environment
   * @param {object} opts.context - Context object passed by om2-form
   * @returns {lit-template} Returns a lit template for the appropriate form field
   */
  static fieldTemplate({ schema, key, data, env, context }) {
    const fieldType = schemaFieldType(schema);
    if (!this.registeredFields[fieldType])
      return renderError(`Unknown schema type="${schema.type}"`);

    const tagName = this.tagName(fieldType);
    return staticHtml`
      <${unsafeStatic(tagName)}
        .key=${key}
        .env=${env}
        .schema=${schema}
        .data=${data}
        .context=${context}
      >
      </${unsafeStatic(tagName)}>
    `;
  }

  /**
   * To save repeating the same field config definitions over and over again (e.g. id,
   * title, tooltip), we define them here and expand them on-the-fly when registering
   * the field.
   *
   * @static
   * @param {object} fields
   * @throws {Error} - An error is thrown if an unknown field is encountered to expand
   * @returns {object} Returns back the same object with values === to SHARED_FIELD expanded with the shared definition
   */
  static expandSharedFields(fields) {
    const sharedFields = {
      id: {
        title: 'Unique Id',
        type: 'string',
        required: true,
      },
      title: {
        title: 'Title',
        type: 'string',
      },
      tooltip: {
        title: 'Tooltip',
        type: 'string',
      },
      class: {
        title: 'CSS Class',
        type: 'string',
      },
      default: {
        title: 'Default Value',
        type: 'string',
      },
      placeholder: {
        title: 'Placeholder Text',
        type: 'string',
      },
      required: {
        title: 'Required',
        type: 'boolean',
      },
      disabled: {
        title: 'Disabled',
        type: 'boolean',
      },
      readonly: {
        title: 'Read Only',
        type: 'boolean',
      },
      hasObjectValues: {
        title: 'Has Object Values',
        type: 'boolean',
        tooltip:
          'If the selected values should be stored as just their id, or as the underlying object (including id and name).',
      },
      url: {
        title: 'Optional URL',
        type: 'string',
        tooltip:
          'Turns the element into a link that when clicked opens a new tab with the specified url',
      },
    };
    return Object.fromEntries(
      Object.entries(fields).map(([key, value]) => {
        if (value === SHARED_FIELD) {
          if (!sharedFields[key])
            throw new Error(`Cannot expand unknown shared field: ${key}`);
          return [key, sharedFields[key]];
        }
        return [key, value];
      })
    );
  }

  /**
   * If this object or one of its parents has display:none, return true. An
   * om2-form-field should never be position:fixed so below should work ok.
   *
   * @returns {boolean} Whether this component is hidden.
   */
  _isHidden() {
    return !this.offsetParent;
  }

  /**
   * Disable shadow dom for this component
   */
  createRenderRoot() {
    return this;
  }

  render() {
    throw new Error('Unimplemented in abstract base class');
  }
}
